import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DefaultFormDialogComponent } from '../../../../shared/message/dialogs/default-form-dialog.component';
import { IDocument, ILov } from '../../../../../type_definitions/datatypes';
import { GaValidateService } from '../../../../shared/formly/ga-validate.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { Override } from '../../../../shared/app-decorators';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { LOV_DOCTYPE, LovService } from '../../../../shared/services/lov.service';
import { BPProfilesDocumentsDataService } from './bp-profiles-documents-data.service';
import { util } from '../../../../shared/util';
import * as _ from 'underscore';

export interface IDocumentAddModel {
  doc_desc?:string;
  doc_type_code?:string;
  file?:File;
}

interface IDocumentModel extends IDocument {
  file?:File;
}

@Component({
  selector: 'add-edit-document-dialog',
  templateUrl: '../../../../shared/message/dialogs/default-form-dialog.html'
})
export class AddEditDocumentDialogComponent extends DefaultFormDialogComponent {
  @Input() public bpProfileId:number;
  @Input() public model:IDocumentModel;

  @Output() public onSaved:EventEmitter<IDocument> = new EventEmitter<IDocument>();

  private documentTypeField:IExtraFieldConfigurationObject;
  private fileField:IExtraFieldConfigurationObject;
  private descriptionField:IExtraFieldConfigurationObject;

  constructor(
    private bpProfilesDocumentsDataService:BPProfilesDocumentsDataService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private lovService:LovService,
    protected validateService:GaValidateService,
    protected translationService:TranslationService
  ) {
    super(validateService, translationService);
  }

  @Override(DefaultFormDialogComponent)
  public ngOnInit():void {
    super.ngOnInit();

    // create fields
    this.fields = this.createFields();
    // fill fields
    this.fillFields();
  }

  private fillFields():void {
    this.formlyService.fillField(
      this.documentTypeField,
      this.lovService.list(LOV_DOCTYPE)
        .then((docTypes:ILov[]) => _.sortBy(docTypes, (docType:ILov) => docType.description))
    );
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';

    const fields:IExtraFieldConfigurationObject[] = [
      this.documentTypeField = this.formlyService.aSelect2('documentType')
        .withFormat(this.formattingService.formatDescription)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withExtraOptions({
          templateOptions: {
            trackBy: 'code'
          }
        })
        .build(),
      this.descriptionField = this.formlyService.anInput('description')
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .build()
    ];

    if (this.adding) {
      fields.push(
        this.fileField = this.formlyService.aFileUpload('file')
          .isRequired()
          .withLabelClass(fullWidthClass)
          .withValueClass(fullWidthClass)
          .withUrl(this.bpProfilesDocumentsDataService.getAuthenticatedBaseDocumentUrl(this.bpProfileId))
          .withExtraOptions({
            templateOptions: {
              fileUploadButtonClass: 'btn-default',
              icon: 'fa fa-folder-open-o',
              uploadContext: {},
              autoUpload: false,
              onUploadDone: this.onUploadDone
            }
          })
          .onChange(this.onFileChange)
          .build()
      );
    } else {
      fields.push(
        this.formlyService.aValue('fileName')
          .withLabelClass(fullWidthClass)
          .withValueClass(fullWidthClass)
          .build()
      );
    }

    return fields;
  }

  private onUploadDone = (result:IDocument):void => {
    this.onSaved.emit(result);
    this.saveButtonBusy = false;
  };

  private onFileChange = (file:File) => {
    this.fileField.templateOptions.hint = file?.name;
    if (util.isNullOrUndefinedOrEmpty(this.model?.description)) {
      this.descriptionField.formControl.setValue(file?.name);
    }
  };

  public save = ():void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.saveButtonBusy = true;
        if (!this.adding) {
          this.bpProfilesDocumentsDataService.updateDocument(this.bpProfileId, <IDocument>this.getModel())
            .then((documentResponse:IDocument) => {
              this.onSaved.emit(documentResponse);
            })
            .finally(() => {
              this.saveButtonBusy = false;
            });

        } else {
          const uploadContext:IDocumentAddModel = {
            doc_desc: this.model.description || this.model.file?.name // if description empty, it will fall back to file name
          };
          if (this.model.documentType?.code) {
            uploadContext.doc_type_code = this.model.documentType?.code;
          }
          // signal to formly fileUpload to start upload
          this.fileField.templateOptions.startUpload.emit(uploadContext);
        }
      })
      .catch(util.noop);
  };
}